/* global G */
/* eslint-disable no-unused-vars */
import { curry, getFirstItem } from 'lib/util'
import concatQueryParams, { getRankedQuery } from 'app/_shared/events/search/query/util'
import internal from 'model/organisation/collection/internal'

/**
 * Return a search query that filters the servers response to only includes
 * suggested items that may be an original for the current organisation.
 *
 * NOTE: Will also return certain service items, since their `installedAt`
 * organisation may also be included in the suggestions. However, the event handler
 * that called this function needs to map over the service items to get the
 * organisation itself. See {@link infiniteCandidates}.
 *
 * @param {Gaia.AppModule.Spec} module      app module
 * @param {Gaia.Component.Spec} component   action component
 * @param {Event|object} event              information about the event that triggered this handler
 *
 * @returns {string}
 */
const isCandidate = (module, component, event) => {
  const { organisation = {} } = module[G.MODEL][G.CHILDREN]
  const orgId = organisation?.[G.STATE]?.[G.REF] || null
  const internalOrganisationsId = internal.map(x => x.key.replaceAll(':', '\\:'))

  // address
  const { value: { address: { zip = '', country = '', street = '', city = '' } = {}, name = '' } = {} } = organisation[G.CACHE]

  // equipment
  const { subServiceItems = [] } = module[G.STATE]
  const ownItems = subServiceItems.filter((x) => {
    const installedAt = getFirstItem(x.refs.installedAt)
    return installedAt.key === orgId
  })

  const nameQuery = getRankedQuery(name, '~^3.0')
  const streetQuery = getRankedQuery(street, '~^2.0')
  const cityQuery = getRankedQuery(city, '~^3.0')
  const zipQuery = getRankedQuery(zip, '~^5.0')

  const itemData = ownItems.reduce((acc, key) => {
    const equipment = getFirstItem(key.refs?.equipment)
    equipment?.key && acc.equipment.push(equipment.key)
    key?.value?.serial && acc.serial.push(key.value.serial)

    return acc
  }, { equipment: [], serial: [] })

  const hasIds = itemData.equipment?.length
  const hasSerials = itemData.serial?.length
  const hasItemData = hasIds || hasSerials

  const itemDataStrings = Object.keys(itemData).reduce((acc, key) => {
    const target = acc[key].join(' OR ')
    if (key && target) {
      acc[key] = `${key}:(${target.replaceAll(':', '\\:')})`
    } else {
      delete acc[key]
    }

    return acc
  }, itemData)

  const { term = null } = component[G.STATE]?.[G.META] || {}
  const hasTerm = term && !!term.length

  const addressQuery = !hasTerm
    ? `(address_country:${country} AND (secondary:${zipQuery} OR nameLower:(${nameQuery}) OR rest:(${streetQuery} ${cityQuery})))`
    : null
  const itemDataQuery = hasItemData
    ? `${Object.values(itemDataStrings).join(' OR ')}`
    : null

  const params = {
    type: 'metatype:(organisation OR serviceitem)',
    status: 'AND status:(50)',
    notInternal: `AND NOT tree_parent:(${internalOrganisationsId.join(' OR ')}) AND NOT tree_installedAt:(${internalOrganisationsId.join(' OR ')})`,
    ...!hasTerm || hasItemData
      ? {
        and: 'AND (',
        query: [addressQuery, itemDataQuery].filter(q => !!q).join(' OR '),
        end: ')',
      }
      : {},
    ...orgId && { notMyself: `AND NOT id:(${orgId.replaceAll(':', '\\:')})` },
  }

  return concatQueryParams(params)
}

export default curry(isCandidate)
